import { CompactNumberInput } from 'components/secondaryMarket/live/sidePanel/quoted/priceAdjustement/cells/CompactNumberInput';
import { TextArea } from 'components/shared/TextField/TextArea';
import type { IntlKey } from 'locales';
import { useId, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SecondaryPriceType } from 'state/secondaryMarket/productSearch';

export interface PriceAdjustmentProps {
  priceType: SecondaryPriceType;
  currency?: string;
  xMarginPlusUpfront: number;
  customizedUpfront?: number;
  customizedXMarginAdjustment?: number;
  tradingPrice: number;
  customizedTradingPriceAdjustment?: number;
  canAdjustPrice: boolean;

  comment: string | undefined;
  readonly?: boolean;
  onCommentChanged: (value: string) => void;
  onCustomizedXMarginAdjustmentChanged: (value?: number) => void;
  onCustomizedUpfrontChanged: (value?: number) => void;
  onCustomizedTradingPriceAdjustmentChanged: (value?: number) => void;
  onResetCustomizedUpfront: () => void;
  onResetCustomizedXMargin: () => void;
}

interface ReadonlyFieldProps {
  labelKey: IntlKey;
  value: number;
  unit?: string;
}

function ReadonlyField({ labelKey, value, unit }: ReadonlyFieldProps) {
  const id = useId();
  const { formatNumber } = useIntl();
  return (
    <div className="col input-container">
      <label htmlFor={id}>
        <FormattedMessage id={labelKey} />
      </label>
      <div className="input-group">
        <input id={id} className="form-control" readOnly value={formatNumber(value)} />
        <span className="input-icon-end">{unit}</span>
      </div>
    </div>
  );
}

export function PriceAdjustment({
  priceType,
  currency,
  xMarginPlusUpfront,
  customizedUpfront,
  customizedXMarginAdjustment,
  customizedTradingPriceAdjustment,
  tradingPrice,
  comment,
  canAdjustPrice,
  readonly = false,
  onCommentChanged,
  onCustomizedTradingPriceAdjustmentChanged,
  onCustomizedXMarginAdjustmentChanged,
  onCustomizedUpfrontChanged,
  onResetCustomizedUpfront,
  onResetCustomizedXMargin,
}: PriceAdjustmentProps) {
  const { formatMessage } = useIntl();
  const unit = priceType === 'Percent' ? '%' : currency!;
  const placeholder = useMemo(
    () => formatMessage({ id: 'secondaryMarket.saleMarginInformation.comment.placeholder' }),
    [formatMessage],
  );

  return (
    <div className="mt-3 overflow-hidden">
      <div className="row">
        <ReadonlyField
          labelKey="secondaryMarket.sidePanel.priceAdjustement.tradingPrice"
          value={tradingPrice}
          unit={unit}
        />
        <div className="col input-container">
          <label>
            <FormattedMessage id="secondaryMarket.sidePanel.priceAdjustement.tradingPriceAdj" />
          </label>
          <div data-e2e="trading-price-adj" className="input-group">
            <CompactNumberInput
              value={customizedTradingPriceAdjustment}
              onValueChanged={onCustomizedTradingPriceAdjustmentChanged}
              priceType={priceType}
              precision={6}
              disabled={readonly}
            />
            <span className="input-icon-end">{unit}</span>
          </div>
        </div>
      </div>
      <div className="row">
        <ReadonlyField
          labelKey="secondaryMarket.sidePanel.priceAdjustement.xPlusUpfront"
          value={xMarginPlusUpfront}
          unit={unit}
        />
        <div className="col input-container">
          <label>
            <FormattedMessage id="secondaryMarket.sidePanel.priceAdjustement.upfront" />
          </label>
          <div data-e2e="upfront-side-panel" className="input-group">
            {canAdjustPrice ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={onResetCustomizedUpfront}
                  title={formatMessage({ id: 'secondaryMarket.sidePanel.label.copyUpfrontToUpfront' })}
                >
                  <em className="icon icon-sm">add</em>
                </button>
                <CompactNumberInput
                  value={customizedUpfront}
                  onValueChanged={onCustomizedUpfrontChanged}
                  priceType={priceType}
                  precision={6}
                  disabled={readonly}
                />
              </>
            ) : (
              <input className="form-control" readOnly />
            )}
            <span className="input-icon-end">{unit}</span>
          </div>
        </div>

        <div className="col input-container">
          <label>
            <FormattedMessage id="secondaryMarket.sidePanel.priceAdjustement.extraX" />
          </label>
          <div className="input-group">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={onResetCustomizedXMargin}
              title={formatMessage({ id: 'secondaryMarket.sidePanel.label.copyUpfrontToSalesMargin' })}
              disabled={readonly}
            >
              <em className="icon icon-sm">remove</em>
            </button>
            <CompactNumberInput
              value={customizedXMarginAdjustment}
              onValueChanged={onCustomizedXMarginAdjustmentChanged}
              priceType={priceType}
              precision={6}
              disabled={readonly}
            />
            <span className="input-icon-end">{unit}</span>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="input-container">
          <label htmlFor="message">
            <FormattedMessage id="secondaryMarket.saleMarginInformation.comment" />
          </label>
          <TextArea
            className="form-control"
            placeholder={placeholder}
            value={comment ?? ''}
            onValueChanged={onCommentChanged}
            rows={3}
            disabled={readonly}
          />
        </div>
      </div>
    </div>
  );
}

PriceAdjustment.displayName = 'PriceAdjustment';
